import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Customers.css";

class Customers extends Component {
  state = {
    customers: [],
    formData: {
      id: null,
      full_name: "",
      phone: "",
      email: "",
      dateofbirth: "",
      customer_image: null,
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",
    searchTerm: "",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchCustomers();
  }

  fetchCustomers = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const res = await axios.get("/customers");
      this.setState({
        customers: Array.isArray(res.data?.data) ? res.data.data : [],
        currentPage: 1,
      });
    } catch (err) {
      console.error("Error fetching customers:", err);
      this.setState({
        error:
          err.response?.data?.message ||
          "Failed to load customer list. Please try again later.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
      error: "",
    }));
  };

  handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    this.setState((prev) => ({
      formData: { ...prev.formData, customer_image: file },
      error: "",
    }));
  };

  handleSearchChange = (e) => {
    this.setState({
      searchTerm: e.target.value,
      currentPage: 1,
    });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({
      itemsPerPage: Number(e.target.value),
      currentPage: 1,
    });
  };

  clearSearch = () => {
    this.setState({
      searchTerm: "",
      currentPage: 1,
    });
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        full_name: "",
        phone: "",
        email: "",
        dateofbirth: "",
        customer_image: null,
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  openModal = (mode = "add", customer = null) => {
    const modalEl = document.getElementById("customerModal");
    if (!modalEl || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalEl);

    if (mode === "edit" && customer) {
      this.setState(
        {
          modalMode: "edit",
          formData: {
            id: customer.id,
            full_name: customer.full_name || "",
            phone: customer.phone || "",
            email: customer.email || "",
            dateofbirth: customer.dateofbirth || "",
            customer_image: null,
          },
          error: "",
        },
        () => modal.show()
      );
    } else {
      this.resetForm();
      modal.show();
    }
  };

  closeCustomerModal = () => {
    const modalEl = document.getElementById("customerModal");
    if (modalEl && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  handleCustomerSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.full_name.trim() || !formData.phone.trim()) {
      return this.setState({
        error: "Full name and phone are required.",
      });
    }

    this.setState({ submitting: true, error: "" });

    try {
      const payload = new FormData();
      payload.append("full_name", formData.full_name.trim());
      payload.append("phone", formData.phone.trim());
      payload.append("email", formData.email ? formData.email.trim() : "");
      payload.append("dateofbirth", formData.dateofbirth || "");

      if (formData.customer_image) {
        payload.append("customer_image", formData.customer_image);
      }

      if (modalMode === "add") {
        await axios.post("/customers", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        payload.append("_method", "PUT");
        await axios.post(`/customers/${formData.id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await this.fetchCustomers();
      this.closeCustomerModal();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Customer added successfully!"
            : "Customer updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error("Error saving customer:", err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to save customer.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalEl = document.getElementById("deleteModal");
    if (!modalEl || !window.bootstrap) return;
    new window.bootstrap.Modal(modalEl).show();
  };

  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`/customers/${deleteId}`);
      await this.fetchCustomers();

      const modalEl = document.getElementById("deleteModal");
      if (modalEl && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
      }
      this.setState({ deleteId: null, deleting: false });

      Swal.fire({
        icon: "success",
        title: "Customer deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error("Error deleting customer:", err);
      this.setState({ deleting: false });
      Swal.fire({
        icon: "error",
        title: "Failed to delete customer.",
        text: err.response?.data?.message || "Please try again.",
      });
    }
  };

  getFilteredCustomers = () => {
    const { customers, searchTerm } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    if (!keyword) return customers;

    return customers.filter((customer) => {
      const name = (customer.full_name || "").toLowerCase();
      const email = (customer.email || "").toLowerCase();
      const phone = (customer.phone || "").toLowerCase();

      return (
        name.includes(keyword) ||
        email.includes(keyword) ||
        phone.includes(keyword)
      );
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let p = start; p <= end; p++) {
      pages.push(p);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  goToPage = (page) => {
    const { currentPage } = this.state;
    const filteredCount = this.getFilteredCustomers().length;
    const totalPages = Math.max(1, Math.ceil(filteredCount / this.state.itemsPerPage));

    let nextPage = page;
    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;

    if (typeof nextPage !== "number") return;
    nextPage = Math.min(totalPages, Math.max(1, nextPage));

    if (nextPage !== currentPage) {
      this.setState({ currentPage: nextPage });
    }
  };

  formatDate = (date) => {
    if (!date) return "N/A";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  render() {
    const {
      customers,
      formData,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      currentPage,
      itemsPerPage,
    } = this.state;

    const baseUrl = axios.defaults.baseURL
      ? axios.defaults.baseURL.replace("/api", "")
      : "";

    const filteredCustomers = this.getFilteredCustomers();
    const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, filteredCustomers.length);
    const currentCustomers = filteredCustomers.slice(startIndex, endIndex);
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container customers-management">
        <div className="pc-content">
          <div className="customers-shell">
            {/* Topbar Header */}
            <div className="customers-topbar">
              <div>
                <div className="customers-breadcrumb">
                  <span>Management</span>
                  <i className="ph ph-caret-right" />
                  <strong>Customers</strong>
                </div>
                <h2 className="customers-title">Customer Management</h2>
                <p className="customers-subtitle">
                  Manage customer profiles, contact information, and records.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>New Customer</span>
              </button>
            </div>

            {/* Summary Cards */}
            <div className="customer-summary-grid">
              <div className="customer-summary-card">
                <div className="customer-summary-icon blue">
                  <i className="ph ph-users" />
                </div>
                <div>
                  <span className="summary-label">Total Customers</span>
                  <strong>{customers.length}</strong>
                </div>
              </div>
              <div className="customer-summary-card">
                <div className="customer-summary-icon green">
                  <i className="ph ph-user-circle" />
                </div>
                <div>
                  <span className="summary-label">Filtered Results</span>
                  <strong>{filteredCustomers.length}</strong>
                </div>
              </div>
              <div className="customer-summary-card">
                <div className="customer-summary-icon pink">
                  <i className="ph ph-files" />
                </div>
                <div>
                  <span className="summary-label">Total Pages</span>
                  <strong>{totalPages}</strong>
                </div>
              </div>
            </div>

            {/* Main Content Card */}
            <div className="customer-card">
              <div className="customer-card-header">
                <div>
                  <h4>Customer Directory</h4>
                  <p>
                    {filteredCustomers.length} record
                    {filteredCustomers.length !== 1 ? "s" : ""} available
                  </p>
                </div>
              </div>

              {/* Toolbar with Search and Rows Per Page */}
              <div className="customer-toolbar">
                <div className="customer-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="customer-search"
                    placeholder="Search by name, email or phone..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="customer-search-clear"
                      onClick={this.clearSearch}
                      aria-label="Clear search"
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="customer-filter-group">
                  <label htmlFor="customerRows">Rows</label>
                  <select
                    id="customerRows"
                    className="customer-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {searchTerm && (
                  <button
                    type="button"
                    className="customer-reset-btn"
                    onClick={this.clearSearch}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="customer-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchCustomers}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="customer-loading">
                  <div className="spinner-border" role="status" aria-hidden="true" />
                  <span>Loading customers...</span>
                </div>
              ) : (
                <>
                  <div className="customer-table-wrap">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th className="col-image">Image</th>
                          <th className="col-name">Full Name</th>
                          <th className="col-email">Email</th>
                          <th className="col-phone">Phone</th>
                          <th className="col-dob">Date of Birth</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCustomers.length > 0 ? (
                          currentCustomers.map((customer, index) => (
                            <tr key={customer.id}>
                              <td className="col-number table-index">
                                {startIndex + index + 1}
                              </td>
                              <td className="col-image">
                                {customer.customer_image ? (
                                  <img
                                    src={`${baseUrl}/uploads/customers/${customer.customer_image}`}
                                    alt={customer.full_name || "Customer"}
                                    className="customer-avatar-image"
                                  />
                                ) : (
                                  <div className="customer-avatar-placeholder">
                                    <i className="ph ph-user" />
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="customer-name-cell">
                                  <strong>{customer.full_name || "N/A"}</strong>
                                  <small>BK-CUS-{customer.id}</small>
                                </div>
                              </td>
                              <td>
                                <div className="customer-contact-cell">
                                  <span>
                                    <i className="ph ph-envelope" />
                                    {customer.email || "N/A"}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="customer-contact-cell">
                                  <span>
                                    <i className="ph ph-phone" />
                                    {customer.phone || "N/A"}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="customer-date-cell">
                                  <i className="ph ph-calendar-blank" />
                                  <span>{this.formatDate(customer.dateofbirth)}</span>
                                </div>
                              </td>
                              <td>
                                <div className="customer-actions">
                                  <button
                                    type="button"
                                    className="customer-action-btn edit"
                                    onClick={() => this.openModal("edit", customer)}
                                    title="Edit Customer"
                                    aria-label={`Edit ${customer.full_name}`}
                                  >
                                    <i className="ph ph-note-pencil" />
                                  </button>
                                  <button
                                    type="button"
                                    className="customer-action-btn delete"
                                    onClick={() => this.confirmDelete(customer.id)}
                                    title="Delete Customer"
                                    aria-label={`Delete ${customer.full_name}`}
                                  >
                                    <i className="ph ph-trash" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7">
                              <div className="customer-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-users-three" />
                                </div>
                                <h5>No customers found</h5>
                                <p>
                                  {searchTerm
                                    ? "No customer matches your search criteria."
                                    : "Add your first customer to get started."}
                                </p>
                                {searchTerm ? (
                                  <button
                                    type="button"
                                    className="customer-reset-btn"
                                    onClick={this.clearSearch}
                                  >
                                    Clear Search
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Customer
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Section */}
                  {filteredCustomers.length > 0 && (
                    <div className="customer-pagination-wrap">
                      <div className="customer-pagination-info">
                        Showing <strong>{startIndex + 1}</strong> to{" "}
                        <strong>{endIndex}</strong> of{" "}
                        <strong>{filteredCustomers.length}</strong> customers
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Customers pagination">
                          <ul className="customer-pagination">
                            <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                                aria-label="Previous page"
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>

                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li key={`ellipsis-${index}`} className="ellipsis">
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={safeCurrentPage === page ? "active" : ""}
                                >
                                  <button
                                    type="button"
                                    onClick={() => this.goToPage(page)}
                                    aria-current={
                                      safeCurrentPage === page ? "page" : undefined
                                    }
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}

                            <li
                              className={
                                safeCurrentPage === totalPages ? "disabled" : ""
                              }
                            >
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                                aria-label="Next page"
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add / Edit Customer Modal */}
        <div className="modal fade" id="customerModal" tabIndex={-1} aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content customer-modal">
              <form onSubmit={this.handleCustomerSubmit}>
                <div className="modal-header customer-modal-header">
                  <div>
                    <span className="customer-modal-kicker">
                      {modalMode === "add" ? "Create Record" : "Update Record"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Customer" : "Edit Customer"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="customer-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.closeCustomerModal}
                    aria-label="Close"
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body customer-modal-body">
                  {error && (
                    <div className="customer-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="customer-form-group">
                    <label htmlFor="fullName">
                      Full Name <span>*</span>
                    </label>
                    <div className="customer-input-wrap">
                      <i className="ph ph-user" />
                      <input
                        id="fullName"
                        type="text"
                        name="full_name"
                        className="customer-input"
                        value={formData.full_name}
                        onChange={this.handleChange}
                        placeholder="Enter full name"
                        autoComplete="off"
                        required
                      />
                    </div>
                  </div>

                  <div className="customer-form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="customer-input-wrap">
                      <i className="ph ph-envelope" />
                      <input
                        id="email"
                        type="email"
                        name="email"
                        className="customer-input"
                        value={formData.email}
                        onChange={this.handleChange}
                        placeholder="Enter email address"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="customer-form-group">
                    <label htmlFor="phone">
                      Phone Number <span>*</span>
                    </label>
                    <div className="customer-input-wrap">
                      <i className="ph ph-phone" />
                      <input
                        id="phone"
                        type="text"
                        name="phone"
                        className="customer-input"
                        value={formData.phone}
                        onChange={this.handleChange}
                        placeholder="Enter phone number"
                        autoComplete="off"
                        required
                      />
                    </div>
                  </div>

                  <div className="customer-form-group">
                    <label htmlFor="dateOfBirth">Date of Birth</label>
                    <div className="customer-input-wrap">
                      <i className="ph ph-calendar-blank" />
                      <input
                        id="dateOfBirth"
                        type="date"
                        name="dateofbirth"
                        className="customer-input"
                        value={formData.dateofbirth}
                        onChange={this.handleChange}
                      />
                    </div>
                  </div>

                  <div className="customer-form-group mb-0">
                    <label htmlFor="customerImage">Customer Image</label>
                    <input
                      id="customerImage"
                      type="file"
                      className="customer-input customer-file-input"
                      accept="image/*"
                      onChange={this.handleFileChange}
                    />
                    <small className="customer-help-text">
                      Supports JPG, PNG or standard image formats.
                    </small>
                  </div>
                </div>

                <div className="modal-footer customer-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.closeCustomerModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i
                          className={
                            modalMode === "add" ? "ph ph-plus" : "ph ph-check"
                          }
                        />
                        {modalMode === "add" ? "Add Customer" : "Update Customer"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1} aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Customer?</h5>
                <p>
                  This customer record will be permanently removed. This action
                  cannot be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Customers;
